/* eslint-disable react/no-multi-comp */
import { ZCDCard } from '@zencity/common-ui';
import { IDataItemParams } from '@zencity/reporting-types';
import React, { ReactElement, useMemo } from 'react';
import {
  rakeWeighterExecutionsToSurveyCycles,
  rakeWeighterExecutionToSelectedDateRange,
} from 'utils/rakeWeighterExecutions';
import {
  WidgetGeneratorContextProps,
  WidgetGeneratorContext,
} from 'screens/WidgetGenerator/contexts/WidgetGeneratorContext';
import { ReportBuilderClientData, ReportFilterProps } from 'screens/WidgetGenerator/reportGeneratorComponents/types';
import styles from './WidgetWrappers.module.scss';

export interface ReportBuilderComponentProps {
  params: IDataItemParams;
  isPreview?: boolean;
}

interface ComponentPreviewWrapperProps {
  children: ReactElement;
  // These props are passed from the Report Builder implicitly.
  reportBuilderComponentProps: ReportBuilderComponentProps;
  previewName: string;
}

export const ComponentPreviewWrapper = (props: ComponentPreviewWrapperProps): ReactElement => {
  const { children, reportBuilderComponentProps, previewName } = props;
  const { isPreview } = reportBuilderComponentProps;
  if (!isPreview) {
    return children;
  }
  return (
    <ZCDCard header={<h3>{previewName}</h3>}>
      <div className={styles.previewCard}>{children}</div>
    </ZCDCard>
  );
};

// eslint-disable-next-line max-statements
const buildContextFromReportBuilderParams = (
  clientData: ReportBuilderClientData,
  selectedSurveyGroupId?: number,
  selectedCycleId?: number,
): WidgetGeneratorContextProps => {
  let selectedSurveyGroup;
  let selectedDateRange;
  let cycles;
  if (selectedSurveyGroupId) {
    selectedSurveyGroup = clientData.surveysGroups.find((surveyGroup) => surveyGroup.id === selectedSurveyGroupId);

    const surveyGroupCyclesExecutions = clientData.rakeWeighterExecutionsBySurveyGroup[selectedSurveyGroupId];
    if (selectedCycleId && surveyGroupCyclesExecutions.length > 0) {
      const selectedCycle = surveyGroupCyclesExecutions.find(
        (rakeWeighterExecution) => rakeWeighterExecution.id === selectedCycleId,
      );

      if (selectedCycle) {
        selectedDateRange = rakeWeighterExecutionToSelectedDateRange(selectedCycle);
      }
      cycles = rakeWeighterExecutionsToSurveyCycles(surveyGroupCyclesExecutions);
    }
  }

  if (!selectedSurveyGroup) {
    throw new Error('Missing `selectedSurveyGroup`, please select one to use this component.');
  }
  if (!selectedDateRange) {
    throw new Error('Missing `selectedDateRange`, please select one to use this component.');
  }
  if (!cycles) {
    throw new Error('Missing `cycles`, contact administrator to check the "cycles" filter.');
  }

  return {
    client: clientData.client,
    cycles,
    selectedSurveyGroup,
    selectedDateRange,
  } as WidgetGeneratorContextProps;
};

/**
 * The purpose of this wrapper is to wrap all the "Report Generator" components with the WidgetGeneratorContext
 * so they can freely use the base values as well as any others they may require.
 */
export const withWidgetGeneratorContextForWidgets =
  (Component: React.FC<any>, previewName: string, clientData: ReportBuilderClientData, componentProps?: any) =>
  (reportBuilderComponentProps: ReportBuilderComponentProps) => {
    const { params } = reportBuilderComponentProps;

    let context: WidgetGeneratorContextProps;
    try {
      context = useMemo(
        () =>
          buildContextFromReportBuilderParams(clientData, params.filters?.surveyGroupId, params.filters?.surveysCycles),
        [params.filters?.surveyGroupId, params.filters?.surveysCycles],
      );
    } catch (error) {
      const asError = error as Error;
      return (
        <ComponentPreviewWrapper reportBuilderComponentProps={reportBuilderComponentProps} previewName={previewName}>
          <div>{asError.message}</div>
        </ComponentPreviewWrapper>
      );
    }

    return (
      <WidgetGeneratorContext.Provider value={context}>
        <ComponentPreviewWrapper reportBuilderComponentProps={reportBuilderComponentProps} previewName={previewName}>
          {/* eslint-disable-next-line react/jsx-props-no-spreading */}
          <Component {...reportBuilderComponentProps} {...componentProps} clientData={clientData} />
        </ComponentPreviewWrapper>
      </WidgetGeneratorContext.Provider>
    );
  };

export const withWidgetGeneratorContextForFilters =
  (Component: React.FC<any>, clientData: ReportBuilderClientData, componentProps?: any) =>
  (reportBuilderComponentProps: ReportFilterProps) => {
    const { filterValues } = reportBuilderComponentProps;

    let context: WidgetGeneratorContextProps;
    try {
      context = useMemo(
        () => buildContextFromReportBuilderParams(clientData, filterValues?.surveyGroupId, filterValues?.surveysCycles),
        [filterValues?.surveyGroupId, filterValues?.surveysCycles],
      );
    } catch (error) {
      const asError = error as Error;
      return <div>{asError.message}</div>;
    }

    return (
      <WidgetGeneratorContext.Provider value={context}>
        {/* eslint-disable-next-line react/jsx-props-no-spreading */}
        <Component {...reportBuilderComponentProps} clientData={clientData} {...componentProps} />
      </WidgetGeneratorContext.Provider>
    );
  };
